import { useTranslation } from 'react-i18next';
import { QuickLink } from '@lyse-as/formds-react';
import { ChevronRightIcon, SecurityIcon, ShieldWithHeartIcon } from '@lyse-as/formds-react/lib/icon/common';

import { ContainerFixed } from 'app/components/container';
import { ContainerLayoutMode } from 'app/components/container/container';
import { ActiveCustomerLocationPage } from 'app/pages/page-wrapper';
import { Breakpoint, Typography as T } from 'app/component-library-wave';
import { useLoadNetworkSettings } from 'app/hooks/use-network';
import { navigationService } from 'app/service/navigation/navigation-service';
import { useAppNavigation } from 'app/utils/navigation-utils';
import { Service } from 'app/service/navigation/pages/page-types';
import { config } from 'app/config/config';
import { Card } from 'app/components/card';
import { useEffect, useState } from 'react';
import { LinkButton } from 'app/components/link-button/link-button';
import { useAppDispatch, useAppSelector } from 'app/hooks/redux-thunk';
import { fetchUserContext } from 'app/store/actions/user-context-thunks';
import { Spinner } from 'app/components/spinner';
import { updateCustomerLocationServices } from 'app/store/actions/user-context-actions';
import { FetchStatus } from 'app/store/root-types';
import styles from './security.module.scss';

declare const window: Window & { dataLayer: Record<string, any>[] };

export const Security: ActiveCustomerLocationPage = (props) => {
  const { t } = useTranslation();
  const { customerCrmId: customerId, servicePointId, services, partnerNumber, partnerLegalName } = props.userContext;
  const { refreshingDataStatus, customerLocations } = useAppSelector((state) => state.userContext);
  const partnerHasSecurityFilter = navigationService.PAGES.internetSecurityFilter.partnerVisibility?.partners.some(
    (partner) => partner === props.userContext.partnerNumber,
  );

  const dispatch = useAppDispatch();
  const { goToPath } = useAppNavigation();

  const TRYGG_CONDITIONS = [Service.TryggFSecure, Service.TryggForsikring];
  const [customerHasTrygg, setCustomerHasTrygg] = useState<boolean>(
    services?.some((service) => TRYGG_CONDITIONS.includes(service)),
  );

  const showAvailableProducts = customerHasTrygg || partnerHasSecurityFilter;

  const shouldContextRefresh = true;
  useLoadNetworkSettings({ servicePointId, customerId });

  useEffect(() => {
    dispatch(fetchUserContext(shouldContextRefresh));
  }, []);

  useEffect(() => {
    if (refreshingDataStatus === FetchStatus.FULFILLED && !customerHasTrygg) {
      fireGaViewEvents();
    }
  }, []);

  useEffect(() => {
    if (refreshingDataStatus === FetchStatus.FULFILLED) {
      const matchingLocation = customerLocations.find((loc) => loc.servicePointId === servicePointId);
      if (matchingLocation && 'services' in matchingLocation) {
        dispatch(updateCustomerLocationServices(matchingLocation.services));
        setCustomerHasTrygg(matchingLocation?.services.some((service) => TRYGG_CONDITIONS.includes(service)));
      }
    }
  }, [refreshingDataStatus, servicePointId]);

  // TODO: Replace with common solution when merging AKW-4792
  const fireGaViewEvents = () => {
    window.dataLayer = window.dataLayer || [];
    const funnelView = {
      event: 'funnel_view',
      funnel_category: 'security',
      funnel_step: 'view_trygg',
      step: 1,
      page_context: 'product order',
      page_url: window.location.href,
      partner_id: parseInt(partnerNumber),
      partner_name: partnerLegalName,
      business_unit: 'b2c',
    };

    const viewItemList = {
      event: 'view_item_list',
      partner_id: parseInt(partnerNumber),
      partner_name: partnerLegalName,
      ecommerce: {
        items: [
          {
            item_id: 'RO_SECURITY_PKG',
            item_name: 'Altibox Trygg',
            affiliation: 'mine sider',
            index: 0,
            currency: 'NOK',
            item_brand: 'Altibox',
            item_category1: 'Service',
            item_category2: 'Addon',
            item_category3: 'vas',
            item_category4: 'insurance',
            item_product_type: 'Altibox Trygg',
            item_business_unit: 'b2c',
            price: 0,
            quantity: 1,
            item_partner_id: parseInt(partnerNumber),
            item_partner_name: partnerLegalName,
          },
        ],
      },
    };
    window.dataLayer.push(funnelView);
    window.dataLayer.push(viewItemList);
  };

  // TODO: Replace with common solution when merging AKW-4792
  const fireGaOrderEvents = () => {
    window.dataLayer = window.dataLayer || [];
    const selectItem = {
      event: 'select_item',
      partner_id: parseInt(partnerNumber),
      partner_name: partnerLegalName,
      ecommerce: {
        items: [
          {
            item_id: 'RO_SECURITY_PKG',
            item_name: 'Altibox Trygg',
            affiliation: 'mine sider',
            index: 0,
            currency: 'NOK',
            item_brand: 'Altibox',
            item_category1: 'Service',
            item_category2: 'Addon',
            item_category3: 'vas',
            item_category4: 'insurance',
            item_product_type: 'Altibox Trygg',
            item_business_unit: 'b2c',
            price: 0,
            quantity: 1,
            item_partner_id: parseInt(partnerNumber),
            item_partner_name: partnerLegalName,
            business_unit: 'b2c',
            item_variant: '',
          },
        ],
      },
    };
    window.dataLayer.push(selectItem);
  };

  return (
    <ContainerFixed mode={ContainerLayoutMode.Medium} className={styles.pageContainer}>
      <section>
        <T
          variant="formds-altibox-subtitle-primary"
          component="h1"
          maxBreakpoint={Breakpoint.TABLET}
          bold={true}
          className={styles.heading}
        >
          {t('pages.security.name')}
        </T>
        <T variant="formds-body" component="p" maxBreakpoint={Breakpoint.TABLET}>
          {t('pages.security.paragraph')}
        </T>
      </section>

      {refreshingDataStatus === FetchStatus.PENDING && <Spinner />}

      {showAvailableProducts && refreshingDataStatus === FetchStatus.FULFILLED && (
        <div className={styles.navList} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <T
            variant="formds-common-subtitle-primary"
            component="h2"
            maxBreakpoint={Breakpoint.TABLET}
            bold={true}
            className={styles.pageHeading}
          >
            {t('pages.security.products.heading')}
          </T>
          {customerHasTrygg && (
            <QuickLink
              title={t('pages.security.insurance.name')}
              description={t('pages.security.insurance.paragraphOwn')}
              href={navigationService.PAGES.securityInsurancePackage.url}
              icon={<ShieldWithHeartIcon style={{ color: styles.altiboxPrimary100Main }} />}
              rightIcon={<ChevronRightIcon />}
              onClick={(e) => {
                e.preventDefault();
                goToPath(navigationService.PAGES.securityInsurancePackage.url);
              }}
            />
          )}

          {partnerHasSecurityFilter && (
            <QuickLink
              title={t('pages.security.securityFilter.name')}
              description={t('pages.security.securityFilter.paragraphOwn')}
              href={navigationService.PAGES.internetSecurityFilter.url}
              icon={<SecurityIcon style={{ color: styles.altiboxPrimary100Main }} />}
              rightIcon={<ChevronRightIcon />}
              onClick={(e) => {
                e.preventDefault();
                goToPath(navigationService.PAGES.internetSecurityFilter.url);
              }}
            />
          )}
        </div>
      )}

      {refreshingDataStatus === FetchStatus.FULFILLED && !customerHasTrygg && (
        <Card className={styles.teaserCard} containerClassName={styles.teaserContainer}>
          <div className={styles.teaserContainer}>
            <div>
              <ShieldWithHeartIcon
                width={75}
                height={75}
                className={styles.tryggIcon}
                style={{ color: styles.altiboxPrimary100Main }}
              />
            </div>
            <div className={styles.teaserInformation}>
              <T variant="formds-common-subtitle-primary" component="h2" maxBreakpoint={Breakpoint.TABLET} bold={true}>
                {t('pages.security.insurance.buyHeading')}
              </T>
              <T variant="paragraph2" component="p" maxBreakpoint={Breakpoint.MOBILE}>
                {t('pages.security.insurance.paragraph')}
              </T>
              <LinkButton
                onClick={fireGaOrderEvents}
                url={`//${config.app.azureDomain}/bestill/trygg`}
                variant="primary"
                iconRight="arrowRight"
              >
                {t('pages.security.insurance.buyCta')}
              </LinkButton>
            </div>
          </div>
        </Card>
      )}

      {!partnerHasSecurityFilter && (
        <aside className={styles.teaserContainer}>
          <div>
            <T
              variant="formds-common-subtitle-primary"
              component="h2"
              maxBreakpoint={Breakpoint.TABLET}
              bold={true}
              className={styles.heading}
            >
              {t('pages.security.insurance.teaserHeading')}
            </T>
            <T variant="paragraph2" component="p" maxBreakpoint={Breakpoint.MOBILE}>
              {t('pages.security.insurance.teaserParagraph')}
            </T>
          </div>
        </aside>
      )}
    </ContainerFixed>
  );
};
